// src/pages/SearchPage.jsx
import React, { useState, useContext, useEffect } from 'react';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import { ThemeContext } from '../context/ThemeContext';

const SearchPage = () => {
  const { theme } = useContext(ThemeContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState({
    tags: [],
    dateRange: 'all',
    type: 'all',
  });

  // Mock data
  const mockNotes = [
    { id: 1, title: 'Meeting Notes - Q4 Planning', content: 'Discussed Q4 goals and milestones for project development. Need to finalize budget by Friday.', tags: ['work', 'meeting', 'planning'], date: '2024-01-15', type: 'meeting' },
    { id: 2, title: 'Biology Class Notes', content: 'Cell structure and functions overview. Mitochondria are the powerhouse of the cell.', tags: ['study', 'biology', 'science'], date: '2024-01-14', type: 'study' },
    { id: 3, title: 'Project Ideas', content: 'Ideas for new features and improvements for the app. Consider adding dark mode and better search functionality.', tags: ['personal', 'ideas', 'app'], date: '2024-01-13', type: 'personal' },
    { id: 4, title: 'React Hooks Tutorial', content: 'Notes on React hooks useState and useEffect. Hooks let you use state in functional components.', tags: ['programming', 'react', 'tutorial'], date: '2024-01-12', type: 'study' },
    { id: 5, title: 'Weekly Team Sync', content: 'Discussed progress on current projects and blockers. Everyone should update their tasks by EOD.', tags: ['work', 'meeting', 'team'], date: '2024-01-11', type: 'meeting' },
  ];

  const allTags = [...new Set(mockNotes.flatMap(note => note.tags))];

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      const query = searchQuery.toLowerCase();
      const filtered = mockNotes.filter(note => {
        const matchesQuery = 
          note.title.toLowerCase().includes(query) ||
          note.content.toLowerCase().includes(query) ||
          note.tags.some(tag => tag.toLowerCase().includes(query));
        
        const matchesTags = filters.tags.length === 0 || 
          filters.tags.some(tag => note.tags.includes(tag));
        
        const matchesType = filters.type === 'all' || note.type === filters.type;
        
        return matchesQuery && matchesTags && matchesType;
      });
      
      setSearchResults(filtered);
      setIsSearching(false);
    }, 500);
  };

  const toggleTagFilter = (tag) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  useEffect(() => {
    if (searchQuery) {
      handleSearch();
    }
  }, [filters]);

  return (
    <div className={`search-page ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 w-full overflow-x-hidden">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Search Notes</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Search through all your notes by content, tags, or keywords
              </p>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search notes by keywords, tags, or content..."
                  className="w-full p-4 pl-12 pr-40 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-4 top-4 text-gray-400">🔍</span>
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="absolute right-2 top-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition disabled:opacity-50"
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Filters Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4">Filters</h3>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tags</h4>
                    <div className="space-y-2">
                      {allTags.map(tag => (
                        <label key={tag} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.tags.includes(tag)}
                            onChange={() => toggleTagFilter(tag)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-gray-700 dark:text-gray-300">{tag}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Note Type</h4>
                    <select
                      value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                      className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    >
                      <option value="all">All Types</option>
                      <option value="meeting">Meeting Notes</option>
                      <option value="study">Study Notes</option>
                      <option value="personal">Personal Notes</option>
                    </select>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Date Range</h4>
                    <select
                      value={filters.dateRange}
                      onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                      className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                    </select>
                  </div>

                  <button
                    onClick={() => {
                      setFilters({
                        tags: [],
                        dateRange: 'all',
                        type: 'all',
                      });
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="w-full mt-6 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>

              {/* Search Results */}
              <div className="lg:col-span-3">
                {searchQuery && (
                  <div className="mb-6">
                    <p className="text-gray-600 dark:text-gray-300">
                      Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
                    </p>
                  </div>
                )}

                {isSearching ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">Searching your notes...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="space-y-6">
                    {searchResults.map(note => (
                      <div key={note.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{note.title}</h3>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{note.date}</span>
                        </div>
                        
                        <div className="flex gap-2 mb-4">
                          {note.tags.map(tag => (
                            <span
                              key={tag}
                              className={`px-3 py-1 text-xs rounded-full ${
                                searchQuery.toLowerCase().includes(tag.toLowerCase())
                                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                          {note.content.length > 200
                            ? note.content.substring(0, 200) + '...'
                            : note.content}
                        </p>
                        
                        <div className="flex gap-3">
                          <button className="text-blue-600 dark:text-blue-400 hover:underline">
                            Open Note
                          </button>
                          <button className="text-gray-600 dark:text-gray-400 hover:underline">
                            Create Flashcard
                          </button>
                          <button className="text-gray-600 dark:text-gray-400 hover:underline">
                            Generate Summary
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : searchQuery ? (
                  <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                    <p className="text-gray-600 dark:text-gray-300">No results found for "{searchQuery}"</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Try different keywords or adjust your filters
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                    <div className="text-4xl mb-4">🔍</div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Start Searching</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      Enter keywords to search through all your notes
                    </p>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                      {['meeting notes', 'biology', 'react hooks', 'project ideas'].map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => setSearchQuery(suggestion)}
                          className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                        >
                          <span className="text-gray-700 dark:text-gray-300">Search: </span>
                          <span className="font-medium text-blue-600 dark:text-blue-400">{suggestion}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SearchPage;